import React from "react";
import {
  Box,
  Dialog,
  DialogProps,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IsRenderableReactNode } from "modules/utility/utility";

/** Above the legacy Modal (99990) and app header (99999). Use an inline style so MUI's Modal z-index cannot override it. */
export const NESTED_DIALOG_Z_INDEX = 100000;
/** Popper-based overlays (Select menu, Tooltip, etc.) above a forceOnTop dialog. */
export const NESTED_DIALOG_POPPER_Z_INDEX = NESTED_DIALOG_Z_INDEX + 1;

export interface ModalDialogBaseProps {
  //MUI Dialog props that are passed through
  open: DialogProps["open"];
  fullWidth?: DialogProps["fullWidth"]; //If true, forces dialog to maxWidth
  maxWidth?: DialogProps["maxWidth"]; //Sets the size of the dialog. Defaults to default to disable.

  //Emergency force of the dialog on top of everything else. Defaults to false.
  //Not recommended unless you absolutely have to (i.e. Nested dialogs)
  forceOnTop?: boolean;

  //Dialog Title
  title?: string;

  //MUI Dialog onClose. If supplied, it controls all close behavior
  //(close icon click, ESC key, backdrop click unless disabled).
  onClose?: () => void;

  //Pass-through of MUI Dialog prop. If true, ESC will not close the dialog.
  disableEscapeKeyDown?: DialogProps["disableEscapeKeyDown"];

  //If true, clicking on the backdrop will NOT close the dialog.
  //This is enforced inside our onClose handler by ignoring reason === "backdropClick".
  disableCloseOnBackdropClick?: boolean;

  contentSection?: string | React.ReactNode;
  actionsSection?: React.ReactNode;
  statusSection?: string | React.ReactNode;
  resultSection?: string | React.ReactNode;
}

export const ModalDialogBase: React.FC<ModalDialogBaseProps> = ({
  open,
  fullWidth = false,
  maxWidth = false,
  forceOnTop = false,
  title = "",

  onClose,
  disableEscapeKeyDown = false,
  disableCloseOnBackdropClick = false,

  contentSection,
  actionsSection,
  statusSection,
  resultSection,
}) => {
  const hasContent = IsRenderableReactNode(contentSection);
  const hasStatus = IsRenderableReactNode(statusSection);
  const hasResult = IsRenderableReactNode(resultSection);
  const hasActions = IsRenderableReactNode(actionsSection);

  const handleDialogClose = React.useCallback<
    NonNullable<DialogProps["onClose"]>
  >(
    (event, reason) => {
      //If disableCloseOnBackdropClick = true, stop clicks on the backdrop from closing the dialog.
      if (disableCloseOnBackdropClick && reason === "backdropClick") return;

      //Call the supplied onClose callback from parent
      onClose?.();
    },
    [disableCloseOnBackdropClick, onClose]
  );

  const handleCloseIconClick = React.useCallback(() => {
    onClose?.();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onClose={handleDialogClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      scroll="paper"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      style={forceOnTop ? { zIndex: NESTED_DIALOG_Z_INDEX } : undefined}
    >
      {/* Dialog Header */}
      <DialogTitle
        id="dialog-title"
        sx={{
          paddingY: 0,
          backgroundColor: "divider",
          marginBottom: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>{title}</Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseIconClick}
            edge="end"
            sx={(theme) => ({
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      {(hasContent || hasStatus || hasResult) && (
        <DialogContent id="dialog-content">
          {/* Main Content Section */}
          {hasContent && (
            <Box>
              {typeof contentSection === "string" ? (
                <DialogContentText>{contentSection}</DialogContentText>
              ) : (
                contentSection
              )}
            </Box>
          )}

          {/* Optional Status Section */}
          {hasStatus && (
            <Box sx={{ mt: hasContent ? 2 : 0 }}>
              {typeof statusSection === "string" ? (
                <Typography variant="body2" color="text.secondary">
                  {statusSection}
                </Typography>
              ) : (
                statusSection
              )}
            </Box>
          )}

          {/* Optional Result Section for showing result of action */}
          {hasResult && (
            <Box sx={{ mt: hasContent || hasStatus ? 2 : 0 }}>
              {typeof resultSection === "string" ? (
                <Typography variant="body2" color="text.secondary">
                  {resultSection}
                </Typography>
              ) : (
                resultSection
              )}
            </Box>
          )}
        </DialogContent>
      )}

      {/* Optional Action Buttons */}
      {hasActions && (
        <DialogActions
          id="dialog-actions"
          sx={{ borderTop: 1, borderColor: "divider" }}
        >
          {actionsSection}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ModalDialogBase;
